import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Container from 'reactstrap/lib/Container';
import { FLAVORS } from 'site-modules/shared/constants/flavors';
import { flavorStore } from 'client/utils/flavor';
import { getIsAppraisalLinkExcluded } from 'site-modules/shared/utils/appraisal/check-links-visible';
import { AiLink } from 'site-modules/shared/components/ai-link/ai-link';
import { useCoDriverScope } from 'site-modules/shared/hooks/use-co-driver-scope';

import { GlobalNavigationMobileWrapper } from './global-navigation-mobile-wrapper';
import { GlobalNavigationMobile } from './global-navigation-mobile';
import { GlobalNavigationDesktop } from './global-navigation-desktop';
import { DesktopLogoAndSearch } from './desktop-logo-and-search';
import { DesktopNavigationLinksCol } from './desktop-navigation-links-col';
import { DesktopInsiderSection } from './desktop-insider-section';
import { LogoSection } from './logo-section';
import { SearchBox } from './search-box';

import './global-navigation.scss';

function GlobalNavigationUI({
  isMobile = false,
  showSearchBar = true,
  pathname = '',
  pageName = null,
  selectedFacets = {},
  renderLogo = undefined,
  searchBarComponent = undefined,
  isLaurelBg = false,
  useGlassTheme = false,
  withAiLink = true,
}) {
  const isAppraisalLinkExcluded = getIsAppraisalLinkExcluded(pageName, selectedFacets);

  const showSearchBarForEntry =
    showSearchBar && !flavorStore.has(FLAVORS.NO_SEARCH) && !flavorStore.has(FLAVORS.MIN_NAV);
  const minimalisticNav = flavorStore.has(FLAVORS.MIN_NAV);
  const hideGlobalNav = useCoDriverScope();

  return (
    <div
      className={classnames('global-navigation w-100', {
        'is-laurel-bg': isLaurelBg,
        'is-desktop': !isMobile && isLaurelBg,
        'use-glass-theme': useGlassTheme,
        'd-none': hideGlobalNav,
      })}
      data-tracking-parent="edm-entry-global-nav"
    >
      <Container className="px-0 keep-style-in-critical-even-if-not-in-dom">
        {isMobile ? (
          <GlobalNavigationMobileWrapper>
            {({ toggleNav, isOpen, preventCloseClassName }) => (
              <GlobalNavigationMobile
                pathname={pathname}
                isAppraisalLinkExcluded={isAppraisalLinkExcluded}
                withAiLink={withAiLink}
                isOpen={isOpen}
              >
                <LogoSection
                  smallScreen
                  toggleNav={toggleNav}
                  isOpen={isOpen}
                  className={preventCloseClassName}
                  renderLogo={renderLogo}
                  useGlassTheme={useGlassTheme}
                />

                {showSearchBarForEntry && (
                  <SearchBox
                    searchBarComponent={searchBarComponent}
                    isOpenMenu={isOpen}
                    withAiLink={withAiLink}
                    isMobile
                  />
                )}
                {!minimalisticNav && !showSearchBarForEntry && withAiLink && <AiLink />}
              </GlobalNavigationMobile>
            )}
          </GlobalNavigationMobileWrapper>
        ) : (
          <GlobalNavigationDesktop>
            <DesktopLogoAndSearch
              showSearchBar={showSearchBarForEntry && !withAiLink}
              renderLogo={renderLogo}
              useGlassTheme={useGlassTheme}
              withAiLink={withAiLink}
            />
            {!minimalisticNav && (
              <>
                <DesktopNavigationLinksCol
                  pathname={pathname}
                  isAppraisalLinkExcluded={isAppraisalLinkExcluded}
                  showSearchBar={showSearchBarForEntry && withAiLink}
                  withAiLink={withAiLink}
                />
                <DesktopInsiderSection />
              </>
            )}
          </GlobalNavigationDesktop>
        )}
      </Container>
    </div>
  );
}

GlobalNavigationUI.propTypes = {
  pathname: PropTypes.string,
  isMobile: PropTypes.bool,
  showSearchBar: PropTypes.bool,
  pageName: PropTypes.string,
  selectedFacets: PropTypes.shape({}),
  renderLogo: PropTypes.func,
  searchBarComponent: PropTypes.elementType,
  isLaurelBg: PropTypes.bool,
  useGlassTheme: PropTypes.bool,
  withAiLink: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  pathname: get(state, 'pageContext.location.pathname'),
  pageName: get(state, 'pageContext.page.name'),
  selectedFacets: get(state, 'inventory.searchResultsFilter'),
});

export const GlobalNavigation = connect(mapStateToProps)(GlobalNavigationUI);
